import { isEmail, isString } from 'class-validator';
import { isNil } from 'lodash';

import {
  CustomFieldType,
  isNilOrBlankString,
  isPhoneValid,
  t,
  toFiniteNumberOrNull
} from '@nova/core';

type ValidationRule = (value: unknown) => boolean | string;

/** Options for creating custom field validation rules (third argument to createCustomFieldRules). */
export interface CreateCustomFieldRulesOptions {
  required: boolean;
  minLengthOrValue?: number | null;
  maxLengthOrValue?: number | null;
}

/**
 * Same keys, and — for required/email/phone — the same underlying check
 * (isEmail from class-validator, isPhoneValid from @nova/core) as
 * Validator.vue's rules.customField(), which Helios registers as $validator.
 * That plugin is satellite code but only installed globally by helios/luna, so
 * these rules are reproduced directly rather than depending on it.
 */
export const messages = {
  /**
   * A full translated sentence rather than the label joined to a translated "Required": that
   * concatenation fixed English word order and broke gender agreement ("Data Obrigatório"). The
   * label is already shown right above the input, so the message loses no context.
   */
  required: () => t('global.errors.thisFieldIsRequired'),
  invalidEmail: () => t('global.errors.emailMustBeValid'),
  invalidPhone: () => t('global.errors.pleaseEnterAValidPhoneNumber'),
  wholeNumber: () => t('global.errors.mustBeAWholeNumber'),
  invalidHour: () => t('global.errors.invalidHour'),
  invalidMinute: () => t('global.errors.invalidMinute'),
  selectAmOrPm: () => t('global.errors.pleaseSelectAmOrPm'),
  minLength: (min: number) => t('global.errors.mustBeAtLeastXCharacters', { min }),
  maxLength: (max: number) => t('global.errors.mustBeAtMostXCharacters', { max }),
  minValue: (min: number) => t('global.errors.mustBeNumberGreaterThan', { min }),
  maxValue: (max: number) => t('global.errors.mustBeNumberLessThan', { max })
};

/**
 * Reproduces Validator.vue's `time` / `time24` for the same reason as the rules below: Circe and
 * Juno never install that plugin. Emptiness is left to the required rule, as in every type rule.
 */
export function createClockTimeRules(isMilitaryTimeEnabled: boolean): Array<ValidationRule> {
  const minHour = isMilitaryTimeEnabled ? 0 : 1;
  const maxHour = isMilitaryTimeEnabled ? 23 : 12;

  return [
    (value: unknown): boolean | string => {
      if (!value || !isString(value)) {
        return true;
      }
      const [hourText, minuteText] = value.split(':');
      const hour = parseInt(hourText, 10);
      const minute = isNilOrBlankString(minuteText) ? 0 : parseInt(minuteText, 10);

      if (Number.isNaN(hour) || hour < minHour || hour > maxHour) {
        return messages.invalidHour();
      }
      return Number.isNaN(minute) || minute < 0 || minute > 59 ? messages.invalidMinute() : true;
    }
  ];
}

/**
 * This is a pure function that returns Vuetify-compatible validation rules.
 * Used by the useCustomFieldValidation composable and can be used directly
 * where needed.
 *
 * @param fieldLabel - @deprecated No longer read: the required message is field-neutral. Kept so
 *   existing callers keep compiling.
 * @param type - The custom field type (from CustomFieldType enum)
 * @param options - Object containing required flag and optional min/max constraints
 * @returns Array of validation functions compatible with Vuetify :rules prop
 */
export function createCustomFieldRules(
  fieldLabel: string,
  type: CustomFieldType,
  options: CreateCustomFieldRulesOptions
): Array<ValidationRule> {
  const { required } = options;

  // Normalize min/max to a finite number or null ("no constraint"). Legacy Helios data can
  // persist '' (empty string) for blank min/max length inputs; '' must not be treated as a
  // real constraint, otherwise e.g. value.length <= '' coerces to <= 0 and every value fails.
  // (OD-3771)
  const minLengthOrValue = toFiniteNumberOrNull(options.minLengthOrValue);
  const maxLengthOrValue = toFiniteNumberOrNull(options.maxLengthOrValue);

  const rules: Array<ValidationRule> = [];

  // Required validation — Vuetify expects each rule to return true (boolean) or a string.
  if (required) {
    const requiredMsg = messages.required();
    rules.push((v: unknown): boolean | string => {
      if (Array.isArray(v)) {
        return v.length > 0 ? true : requiredMsg;
      }
      if (typeof v === 'boolean') {
        return true;
      }
      if (typeof v === 'number') {
        return Number.isFinite(v) ? true : requiredMsg;
      }
      return v ? true : requiredMsg;
    });
  }

  // Type-specific validations
  if (type === CustomFieldType.Email) {
    rules.push((v: unknown): boolean | string => {
      if (!v) {
        return true;
      }
      return typeof v === 'string' && isEmail(v) ? true : messages.invalidEmail();
    });
  }

  if (type === CustomFieldType.Phone) {
    rules.push((v: unknown): boolean | string => {
      if (!v) {
        return true;
      }
      return typeof v === 'string' && isPhoneValid(v) ? true : messages.invalidPhone();
    });
  }

  /**
   * The `int` type holds whole numbers (isValidCustomField checks isInt), but a type="number"
   * input still takes a pasted or typed decimal, and the legacy appointment.customFields save
   * path runs no type check, so the value would persist as-is.
   */
  if (type === CustomFieldType.Number) {
    rules.push((v: unknown): boolean | string => {
      if (isNilOrBlankString(v)) {
        return true;
      }
      return Number.isInteger(Number(v)) ? true : messages.wholeNumber();
    });
  }

  // Length/range validations. min/max are already normalized to a finite number or null
  // above, so any non-null value here is a real constraint.
  if (!isNil(minLengthOrValue) || !isNil(maxLengthOrValue)) {
    if (type === CustomFieldType.String || type === CustomFieldType.BigString) {
      if (!isNil(minLengthOrValue)) {
        const minLen = minLengthOrValue;
        const minLenMsg = messages.minLength(minLen);
        rules.push((v: unknown): boolean | string => {
          if (!v) {
            return true;
          }
          return typeof v === 'string' && v.length >= minLen ? true : minLenMsg;
        });
      }
      if (!isNil(maxLengthOrValue)) {
        const maxLen = maxLengthOrValue;
        const maxLenMsg = messages.maxLength(maxLen);
        rules.push((v: unknown): boolean | string => {
          if (!v) {
            return true;
          }
          return typeof v === 'string' && v.length <= maxLen ? true : maxLenMsg;
        });
      }
    } else if (type === CustomFieldType.Number) {
      if (!isNil(minLengthOrValue)) {
        const minVal = minLengthOrValue;
        const minValMsg = messages.minValue(minVal);
        rules.push((v: unknown): boolean | string => {
          if (v === '' || isNil(v)) {
            return true;
          }
          return Number(v) >= minVal ? true : minValMsg;
        });
      }
      if (!isNil(maxLengthOrValue)) {
        const maxVal = maxLengthOrValue;
        const maxValMsg = messages.maxValue(maxVal);
        rules.push((v: unknown): boolean | string => {
          if (v === '' || isNil(v)) {
            return true;
          }
          return Number(v) <= maxVal ? true : maxValMsg;
        });
      }
    }
  }

  return rules;
}
